// Grafana Faro — real user monitoring into the platform's own observability
// stack (Alloy -> Loki -> Grafana), rather than a third-party SaaS.
//
// WHY THIS EXISTS ALONGSIDE telemetry.ts (OpenObserve): that integration is in
// the codebase but INERT in production — no OpenObserve client token is baked
// into the prod build, so its guard in main.ts never fires and nothing is
// collected. This reports to https://rum.prod.socommerz.in, the same stack that
// already holds the service metrics and logs.
//
// Never run both. Two RUM SDKs mean two beacon streams, two bundles, and two
// places to look.
//
// WHAT IT ADDS: ingress metrics stop at the last byte written. A merchant
// reporting "the admin is slow" or "the page went blank" is describing something
// the cluster cannot see — layout, hydration, and JS exceptions on their machine.
//
// PRIVACY: the collector is a PUBLIC endpoint, so everything sent is stored.
// Admin URLs carry shop and order identifiers in the path and tokens in the query
// string, so URLs are scrubbed to origin + pathname, and no user identity is
// attached.
import type { Faro } from '@grafana/faro-web-sdk';

let faro: Faro | null = null;

export interface FaroOptions {
  url: string;
  appName: string;
  appVersion: string;
  environment: string;
  /** Fraction 0..1, not a percentage. */
  sampleRate?: number;
}

function scrubUrl(raw: string): string {
  try {
    const u = new URL(raw);
    return `${u.origin}${u.pathname}`;
  } catch {
    return raw.split('?')[0].split('#')[0];
  }
}

export async function initFaro(opts: FaroOptions): Promise<boolean> {
  if (faro) return true;
  if (typeof window === 'undefined') return false;

  try {
    // Dynamic import: an environment without a collector configured never even
    // downloads the SDK.
    const { initializeFaro, getWebInstrumentations } = await import('@grafana/faro-web-sdk');

    faro = initializeFaro({
      url: opts.url,
      app: {
        name: opts.appName,
        version: opts.appVersion,
        environment: opts.environment
      },
      sessionTracking: { samplingRate: opts.sampleRate ?? 1 },
      instrumentations: getWebInstrumentations({
        // The admin logs a lot at debug level; shipping console output would turn
        // a telemetry endpoint into a log firehose.
        captureConsole: false
      }),
      beforeSend: (item: any) => {
        try {
          if (item?.meta?.page?.url) item.meta.page.url = scrubUrl(item.meta.page.url);
          if (item?.payload?.context?.url) item.payload.context.url = scrubUrl(item.payload.context.url);
        } catch {
          /* scrubbing must never break the app it is observing */
        }
        return item;
      }
    });

    return true;
  } catch {
    // RUM is diagnostics. A failed init must never stop the admin from loading.
    return false;
  }
}

export function getFaro(): Faro | null {
  return faro;
}
